import { useEffect, useState } from 'react';
import { db } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { cn } from '@/lib/cn';
import { ChevronDown, ChevronUp, Clock, Check, Edit3 } from 'lucide-react';
import { TimestampInput } from '@/components/TimestampInput';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoIncidentFields } from '@/lib/demo-data';
import type { Incident } from '@/types';

/**
 * TimestampRail — run times, always accessible from inside the ePCR.
 *
 * Problem: the 11 NEMSIS + fire run timestamps live on the Incident,
 * not the ePCR, which means the provider has to navigate out of the
 * ePCR to stamp "on-scene" or "transport begin". Under stress this
 * gets skipped; at QA we see call logs with missing timestamps.
 *
 * Fix: pin a compact horizontal strip to the top of the ePCR page.
 * Each of the 11 timestamps is a chip that shows HH:MM:SS when set,
 * or a "tap to stamp" prompt when not. Tap the chip → timestamps
 * NOW with one tap. Tap the edit icon → opens a TimestampInput for
 * a specific time (e.g., correcting a missed stamp).
 *
 * Source badge (CAD / PSAP / MANUAL) mirrors the TimesTab logic so
 * the provenance is visible without leaving the ePCR. Manual edits
 * remove the field from cad_managed_fields + stamp user_edited_at,
 * identical to the TimesTab behavior.
 *
 * Collapsible: starts open on first view, can be folded to a single-
 * row summary ("Dispatched ✓ · En-Route ✓ · On-Scene ✓ · At-Patient
 * —") that still lets the next-expected stamp be tapped in one gesture.
 */

const FIELDS: Array<{
  key: keyof Incident & string;
  short: string;
  full: string;
  defaultSource: 'CAD' | 'PSAP';
}> = [
  { key: 'call_received_time',               short: 'Rcvd',    full: 'Call Received',        defaultSource: 'PSAP' },
  { key: 'call_entered_time',                short: 'Ent',     full: 'Call Entered',         defaultSource: 'CAD' },
  { key: 'dispatch_time',                    short: 'Disp',    full: 'Dispatched',           defaultSource: 'CAD' },
  { key: 'en_route_time',                    short: 'En-Rt',   full: 'En Route',             defaultSource: 'CAD' },
  { key: 'on_scene_time',                    short: 'Scene',   full: 'On Scene',             defaultSource: 'CAD' },
  { key: 'at_patient_time',                  short: 'Pt',      full: 'At Patient',           defaultSource: 'CAD' },
  { key: 'transport_time',                   short: 'Trans',   full: 'Transport Begin',      defaultSource: 'CAD' },
  { key: 'at_hospital_time',                 short: 'Hosp',    full: 'At Hospital',          defaultSource: 'CAD' },
  { key: 'transfer_care_other_service_time', short: 'TOC',     full: 'Care Transferred',     defaultSource: 'CAD' },
  { key: 'available_time',                   short: 'Avail',   full: 'Available',            defaultSource: 'CAD' },
  { key: 'back_in_quarters_time',            short: 'Qtrs',    full: 'In Quarters',          defaultSource: 'CAD' },
];

export function TimestampRail({ incidentId }: { incidentId: string }) {
  const [incident, setIncident] = useState<Incident | null>(null);
  const [open, setOpen] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const inc = await db.incidents.get(incidentId);
      if (alive) setIncident(inc ?? null);
    })();
    return () => { alive = false; };
  }, [incidentId]);

  async function stamp(key: keyof Incident & string, when: string | null) {
    if (!incident) return;
    const now = new Date().toISOString();
    const userEditedAt: Record<string, string> = { ...(incident.user_edited_at ?? {}) };
    const cadManagedFields = [...(incident.cad_managed_fields ?? [])];
    if (when !== null) {
      userEditedAt[key] = now;
      const idx = cadManagedFields.indexOf(key);
      if (idx >= 0) cadManagedFields.splice(idx, 1);
    }
    const updated: Incident = {
      ...incident,
      [key]: when,
      user_edited_at: userEditedAt,
      cad_managed_fields: cadManagedFields,
    };
    setIncident(updated);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await db.incidents.update(incident.id, {
      [key]: when,
      user_edited_at: userEditedAt,
      cad_managed_fields: cadManagedFields,
      _dirty: true,
      _last_modified: now,
    } as any);
    await enqueueSync('incidents', incident.id, 'update', {
      id: incident.id, [key]: when, user_edited_at: userEditedAt, cad_managed_fields: cadManagedFields, updated_at: now,
    });
  }

  if (!incident) return null;

  // Collapsed view: single-row summary with tap-to-stamp on the next-expected field.
  if (!open) {
    const setCount = FIELDS.filter((f) => !!incident[f.key]).length;
    return (
      <div className="border border-slate-700 bg-slate-900/70">
        <button
          onClick={() => setOpen(true)}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-800"
        >
          <Clock size={13} className="text-blue-400 shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">
            Times · {setCount}/{FIELDS.length}
          </span>
          <div className="flex-1 flex gap-0.5 overflow-x-auto scrollbar-hide">
            {FIELDS.map((f) => {
              const ts = incident[f.key] as string | null;
              return (
                <span
                  key={f.key}
                  className={cn(
                    'px-1.5 py-0.5 text-[9px] font-mono tabular-nums shrink-0',
                    ts ? 'text-slate-200' : 'text-slate-600',
                  )}
                  title={f.full}
                >
                  {f.short} {ts ? timeShort(ts) : '—'}
                </span>
              );
            })}
          </div>
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </button>
      </div>
    );
  }

  return (
    <div className="border border-slate-700 bg-slate-900/70">
      <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-2.5 py-1 gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-blue-400" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">
            Run Times
          </span>
          <span className="text-[10px] font-mono text-slate-500">
            tap chip to stamp now · tap pencil to edit
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DemoFillButton
            label="Fill times demo"
            onFill={async () => {
              if (!incident) return;
              const fields = demoIncidentFields();
              const updated: Incident = { ...incident, ...fields } as Incident;
              setIncident(updated);
              await db.incidents.update(incident.id, {
                ...fields,
                _dirty: true,
                _last_modified: new Date().toISOString(),
              } as never);
              await enqueueSync('incidents', incident.id, 'update', {
                id: incident.id, ...fields,
              });
            }}
          />
          <button
            onClick={() => setOpen(false)}
            className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-200"
          >
            Collapse <ChevronUp size={12} />
          </button>
        </div>
      </div>
      {/* Auto-fit grid: chips wrap based on viewport + content. Each
          chip claims at least 110 px so the time always fits, and
          fills extra space evenly with 1fr. No fixed col-count means
          no overflow at any width. */}
      <div
        className="grid gap-1 p-1.5"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}
      >
        {FIELDS.map((f) => {
          const ts = incident[f.key] as string | null;
          const edited = !!incident.user_edited_at?.[f.key];
          const source: 'CAD' | 'PSAP' | 'MANUAL' | 'NONE' =
            !ts ? 'NONE' : edited ? 'MANUAL' : f.defaultSource;
          const isEditing = editingKey === f.key;
          return (
            <div key={f.key} className="flex flex-col">
              {!isEditing ? (
                /* Minimal chip — event label (top, tiny) + time (big,
                   fills the chip). Single click target. No pencil, no
                   caption, no source-colored border / label clutter.
                   The source is encoded as a thin 2px LEFT-EDGE stripe
                   only — same information, zero visual weight. */
                <button
                  type="button"
                  onClick={() => ts ? setEditingKey(f.key) : void stamp(f.key, new Date().toISOString())}
                  className={cn(
                    /* Compact single-row chip — label inline with time
                       so the chip is short. All rows the same height
                       so the rail reads as a uniform strip. */
                    'flex items-center gap-1.5 px-2 py-1 border text-left transition-colors w-full h-[34px] overflow-hidden',
                    'border-slate-700 bg-slate-900/40 hover:bg-slate-800',
                    'border-l-[3px]',
                    source === 'MANUAL' ? 'border-l-amber-500' :
                    source === 'CAD'    ? 'border-l-blue-500' :
                    source === 'PSAP'   ? 'border-l-emerald-500' :
                                          'border-l-slate-600',
                  )}
                  title={ts ? `${f.full} — tap to edit` : `${f.full} — tap to stamp NOW`}
                >
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap shrink-0">
                    {f.short}
                  </span>
                  <span
                    className={cn(
                      'font-mono tabular-nums font-bold w-full truncate text-right',
                      ts ? 'text-white' : 'text-slate-500',
                    )}
                    style={{ fontSize: 'clamp(13px, 1.6vw, 15px)' }}
                  >
                    {ts ? timeShort(ts) : '—'}
                  </span>
                </button>
              ) : (
                /* Inline edit — native <input type="time"> opens the
                   platform numpad on iOS / Android. Compact: input
                   sits inline with a tiny ✓ button so the chip stays
                   the same 34 px height as the resting state. */
                <div className="flex items-center gap-1 px-1 py-0.5 border border-amber-700 bg-amber-950/30 border-l-[3px] border-l-amber-500 h-[34px]">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300 whitespace-nowrap shrink-0">
                    {f.short}
                  </span>
                  <input
                    type="time"
                    value={ts ? toTimeInput(ts) : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      const next = new Date(ts ?? new Date().toISOString());
                      const [hh, mm] = v.split(':').map(Number);
                      next.setHours(hh, mm, 0, 0);
                      void stamp(f.key, next.toISOString());
                    }}
                    autoFocus
                    className="flex-1 min-w-0 bg-white text-slate-900 font-mono tabular-nums font-bold border border-amber-300 px-1 leading-none rounded-none text-[13px] h-[24px]"
                  />
                  <button
                    onClick={() => setEditingKey(null)}
                    className="bg-amber-600 text-white hover:bg-amber-500 px-1.5 h-[24px] text-[10px] font-bold shrink-0"
                    title="Done editing"
                  >
                    ✓
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function timeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '?';
  const pad = (n: number) => String(n).padStart(2, '0');
  // HH:MM only — run timestamps are minute-precision in practice and
  // the shorter string always fits in the chip at any viewport size.
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO → "HH:MM" for the native <input type="time"> value attribute. */
function toTimeInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
